import { ReactNode } from "react";
import { cn } from "../../../utils/class-names";
import { match } from "ts-pattern";
import { asPercent } from "@silo/utils";
import { SuperfestIcon } from "../../../icons";
import { PositionReward } from "@silo/types";

export const Breakdown = {
  Root,
  Item,
  ItemValue,
  ItemLabel,
  DepositRewardItem,
  DebtRewardItem,
};

interface RootProps {
  className?: string;
  children?: ReactNode;
  align: Align;
}

export type Align = "left" | "right";

function Root({ className, children, align }: RootProps): JSX.Element {
  return (
    <div
      className={cn(
        "flex flex-col gap-2.5",
        match(align)
          .with("left", () => "items-start text-left")
          .with("right", () => "items-end text-right")
          .exhaustive(),
        className
      )}
    >
      {children}
    </div>
  );
}

interface ItemProps {
  className?: string;
  children?: ReactNode;
}

function Item({ className, children }: ItemProps): JSX.Element {
  return (
    <div className={cn("flex items-center gap-2.5", className)}>{children}</div>
  );
}

interface ItemValueProps {
  className?: string;
  children?: ReactNode;
}

function ItemValue({ className, children }: ItemValueProps): JSX.Element {
  return (
    <div className={cn("font-mono font-medium text-white", className)}>
      {children}
    </div>
  );
}

interface ItemLabelProps {
  className?: string;
  children?: ReactNode;
}

function ItemLabel({ className, children }: ItemLabelProps): JSX.Element {
  return <div className={cn("text-shale-200", className)}>{children}</div>;
}

interface DepositRewardItemProps {
  className?: string;
  reward: PositionReward;
}

function DepositRewardItem({
  className,
  reward,
}: DepositRewardItemProps): JSX.Element {
  const { rewardTokenSymbol, apr, isSuperFest } = reward;

  return (
    <Item className={cn("", className)}>
      <ItemValue>{asPercent(apr)}</ItemValue>

      {isSuperFest ? (
        <>
          <SuperfestIcon className="w-4 h-4" />
          <ItemLabel>SuperFest rewards</ItemLabel>
        </>
      ) : (
        <ItemLabel>rewards APR in {rewardTokenSymbol}</ItemLabel>
      )}
    </Item>
  );
}

interface DebtRewardItemProps {
  className?: string;
  reward: PositionReward;
}

function DebtRewardItem({
  className,
  reward,
}: DebtRewardItemProps): JSX.Element {
  const { rewardTokenSymbol, apr, isSuperFest } = reward;

  return (
    <Item className={cn("", className)}>
      <ItemValue>{asPercent(apr)}</ItemValue>

      {isSuperFest ? (
        <>
          <SuperfestIcon className="w-4 h-4" />
          <ItemLabel>SuperFest rewards (yield)</ItemLabel>
        </>
      ) : (
        <ItemLabel>rewards APR in {rewardTokenSymbol} (yield)</ItemLabel>
      )}
    </Item>
  );
}
